// §III footer_where_you_are_now — the render side of the shared currentStanding
// detector (engine: src/insights/render_keys.js, contract s323.d1/s390.d4). The
// engine emits the structural facts; this file selects the CANONICAL §III phrase
// (Field Mapping Spec §III.footer_where_you_are_now, 8 variants, locked s400 + s407).
//
// Contract in:  { touchedCount:T, cloud:{size:M, level:'low'|'mid'|'high', band}|null,
//                 outlier|null, outliers:[{layer, value, dir:'above'|'below'}] }
// Result out:   { state, text, segments } — segments carry the amber layer name and
//               the em forward clause so SectionIII can mark them; text is the plain
//               concatenation (tests / aria).
//
// Numbers are word-spelled; a single broken-out layer is named, two are counted
// (s342.d1: position only, no motion verbs). The forward clause "that separation is
// the patterns chapter." rides only on SEPARATION (variants 1/2 outlier, 7 two-sided,
// 8 mid-partial); convergence (3/4/6) and scatter (5) carry none. §IV shares the
// engine but a different register — its copy lives with the §IV render, not here.

import { LAYERS } from './layersConfig.js';

const NUM_WORD = {
  0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven',
  8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
};
const word = (n) => NUM_WORD[n] ?? String(n);
const layerName = (key) => LAYERS.find((L) => L.key === key)?.name ?? key;
const FWD = { text: 'that separation is the patterns chapter.', em: true };

// Build the result: normalise string parts to {text}; compute plain text + state.
function make(state, parts) {
  const segments = parts.map((p) => (typeof p === 'string' ? { text: p } : p));
  return { state, segments, text: segments.map((s) => s.text).join('') };
}

export function standingState(cs) {
  // hard fallback: 0 touched layers → block omitted by the caller
  if (!cs || cs.touchedCount === 0) return null;
  const T = word(cs.touchedCount);

  if (!cs.cloud) return make('scatter', [`the ${T} pull apart — no two agree.`]); // (5)

  const { level, size } = cs.cloud;
  const M = word(size);
  const outs = cs.outliers ?? [];

  // (3/4/6) cloud, no outliers → all-together at its level
  if (outs.length === 0) {
    if (level === 'low') return make('all-together', [`all ${T} converge to the same low.`]);
    if (level === 'high') return make('all-together', [`all ${T} sit high together.`]);
    return make('all-together', [`all ${T} sit in the middle together.`]); // mid (6)
  }

  // (8) mid cloud + broken-out layers (≤2, since cloud ≥5 & T ≤7) — position only
  if (level === 'mid') {
    const above = outs.filter((o) => o.dir === 'above').length;
    const below = outs.filter((o) => o.dir === 'below').length;
    if (outs.length === 1) {
      const side = outs[0].dir === 'above' ? 'above' : 'below';
      return make('mid-outlier', [`${M} of ${T} sit in the middle together. `, { text: layerName(outs[0].layer), amber: true }, ` is the one ${side} the rest. `, FWD]); // (8a/8b)
    }
    if (above === 2) return make('mid-outlier', [`${M} of ${T} sit in the middle together. ${word(2)} sit above the rest. `, FWD]); // (8c)
    if (below === 2) return make('mid-outlier', [`${M} of ${T} sit in the middle together. ${word(2)} sit below the rest. `, FWD]); // (8d)
    return make('mid-outlier', [`${M} of ${T} sit in the middle together, with one above and one below. `, FWD]); // (8e)
  }

  // (1/2) low/high cloud + exactly one broken-out layer (opposite side, fold rule)
  if (outs.length === 1) {
    if (level === 'low') return make('one-outlier', [`${M} of ${T} converge to the same low. `, { text: layerName(outs[0].layer), amber: true }, ` is the one that doesn't. `, FWD]); // (1)
    return make('one-outlier', [`${M} of ${T} sit high together. `, { text: layerName(outs[0].layer), amber: true }, ` is the one below the rest. `, FWD]); // (2)
  }

  // (7) low/high cloud + 2+ broken out the other way → two-sided, counted not named.
  // Ordering (s407.d1): larger group leads; tie → the high clause leads. Verbs are
  // glued to their side; the mirror only swaps clause order.
  const highSize = level === 'high' ? size : outs.length;
  const lowSize = level === 'low' ? size : outs.length;
  const highLeads = highSize >= lowSize;
  const parts = highLeads
    ? [`${word(highSize)} layers sit high while ${word(lowSize)} converge to the same low. `, FWD]
    : [`${word(lowSize)} layers converge to the same low while ${word(highSize)} sit high. `, FWD];
  return make('two-sided', parts);
}
